// ─────────────────────────────────────────────
// HubWire Mock Database — Demo Organization
// Westland Minor Sports Association (fictional)
// ─────────────────────────────────────────────

import type {
  Organization,
  SiteSettings,
  Event,
  Registration,
  Sponsor,
  Announcement,
  AdminUser,
} from '../types';

export const _mockOrganization: Organization = {
  id: 'org_001',
  name: 'Westland Minor Sports Association',
  slug: 'westland-minor-sports',
  logo_url: '/assets/logo.svg',
  primary_color: '#1a3a5c',
  secondary_color: '#c8451a',
  contact_email: 'info@westlandminorsports.ca',
  phone: '(306) 555-0100',
  address: '45 Arena Drive, Westland, SK S0L 0W0',
  website_url: 'https://westlandminorsports.ca',
  facebook_url: 'https://facebook.com/westlandminorsports',
  public_description:
    'The Westland Minor Sports Association provides hockey and baseball programs for youth ages 4 to 18. We believe every kid deserves ice time and a chance at the plate. Our volunteers have run programs in this community for over 30 years.',
};

export const _mockSiteSettings: SiteSettings = {
  id: 'settings_001',
  organization_id: 'org_001',
  display_name: 'Westland Minor Sports',
  logo_url: '/assets/logo.svg',
  primary_color: '#1a3a5c',
  secondary_color: '#c8451a',
  contact_email: 'info@westlandminorsports.ca',
  phone: '(306) 555-0100',
  address: '45 Arena Drive, Westland, SK S0L 0W0',
  social_links: {
    facebook: 'https://facebook.com/westlandminorsports',
  },
  default_cta_text: 'Register Now',
  footer_text: '© 2026 Westland Minor Sports Association. All rights reserved. Powered by HubWire.',
};

export const _mockEvents: Event[] = [
  {
    id: 'evt_001',
    organization_id: 'org_001',
    title: 'Hockey Registration — 2026/27 Season',
    slug: 'hockey-registration-2026-27',
    type: 'tournament',
    start_date: '2026-09-06T09:00:00',
    end_date: '2026-09-06T14:00:00',
    location: 'Westland Arena, Main Lobby',
    description:
      'Register your child for the 2026/27 hockey season. All divisions open: Initiation (ages 4–6), Novice (7–8), Atom (9–10), Peewee (11–12), Bantam (13–14), and Midget (15–17).\n\nBring proof of age, previous season equipment if available, and payment. Helmet fitting assistance available on site.\n\nEarly bird pricing available until August 20.',
    registration_open: true,
    registration_deadline: '2026-09-01T23:59:00',
    max_entries: 200,
    status: 'published',
    featured_image_url: '/assets/events/hockey-reg.svg',
  },
  {
    id: 'evt_002',
    organization_id: 'org_001',
    title: 'Baseball Registration — Spring 2026',
    slug: 'baseball-registration-spring-2026',
    type: 'tournament',
    start_date: '2026-04-12T10:00:00',
    end_date: '2026-04-12T13:00:00',
    location: 'Westland Community Centre, Room B',
    description:
      'Spring baseball registration for the 2026 season. Divisions: T-Ball (ages 4–6), Coach Pitch (7–8), Minor Ball (9–10), Major Ball (11–12), and Midget (13–15).\n\nAll skill levels welcome. No experience required for younger divisions. Uniforms included with registration fee.',
    registration_open: true,
    registration_deadline: '2026-04-05T23:59:00',
    max_entries: 150,
    status: 'published',
    featured_image_url: '/assets/events/baseball-reg.svg',
  },
  {
    id: 'evt_003',
    organization_id: 'org_001',
    title: 'End of Season Hockey Banquet',
    slug: 'hockey-banquet-2026',
    type: 'social',
    start_date: '2026-03-28T18:00:00',
    end_date: '2026-03-28T21:00:00',
    location: 'Westland Legion Hall',
    description:
      'Celebrate the 2025/26 hockey season with players, families, and coaches. Awards presentation, team photos, and dinner. All divisions welcome.\n\nTickets must be purchased in advance. Players are free, adults $25, children under 6 free.',
    registration_open: true,
    registration_deadline: '2026-03-21T23:59:00',
    max_entries: 300,
    status: 'published',
    featured_image_url: '/assets/events/banquet.svg',
  },
  {
    id: 'evt_004',
    organization_id: 'org_001',
    title: 'Spring Hockey Skills Camp',
    slug: 'spring-skills-camp-2026',
    type: 'workshop',
    start_date: '2026-05-10T09:00:00',
    end_date: '2026-05-11T16:00:00',
    location: 'Westland Arena, Sheets 1 & 2',
    description:
      'Two-day skills development camp for Atom through Bantam players. Focus areas: skating mechanics, puck handling, shooting, and defensive positioning. Led by certified coaches.\n\nLimited to 40 players. Full equipment required.',
    registration_open: true,
    registration_deadline: '2026-05-03T23:59:00',
    max_entries: 40,
    status: 'published',
    featured_image_url: '/assets/events/skills-camp.svg',
  },
  {
    id: 'evt_005',
    organization_id: 'org_001',
    title: 'Annual General Meeting',
    slug: 'agm-2026',
    type: 'meeting',
    start_date: '2026-06-10T19:00:00',
    end_date: '2026-06-10T21:00:00',
    location: 'Westland Community Centre, Main Hall',
    description:
      'All members and parents are encouraged to attend. Election of board positions, review of financials, and planning for the 2026/27 season. Nominations for board positions close June 1.',
    registration_open: false,
    registration_deadline: '2026-06-09T23:59:00',
    max_entries: 0,
    status: 'published',
    featured_image_url: '',
  },
];

export const _mockRegistrations: Registration[] = [
  {
    id: 'reg_001',
    organization_id: 'org_001',
    event_id: 'evt_001',
    name: 'Kevin Marchuk',
    email: 'kmarchuk@example.com',
    phone: '(306) 555-0201',
    team_name: 'Peewee A',
    notes: 'Registering two kids — will need two spots',
    payment_status: 'paid',
    registration_status: 'confirmed',
    created_at: '2026-08-05T10:14:00',
  },
  {
    id: 'reg_002',
    organization_id: 'org_001',
    event_id: 'evt_001',
    name: 'Tanya Friesen',
    email: 'tfriesen@example.com',
    phone: '(306) 555-0312',
    team_name: 'Novice',
    notes: '',
    payment_status: 'pending',
    registration_status: 'confirmed',
    created_at: '2026-08-08T14:30:00',
  },
  {
    id: 'reg_003',
    organization_id: 'org_001',
    event_id: 'evt_002',
    name: 'Dale Siemens',
    email: 'dsiemens@example.com',
    phone: '(306) 555-0423',
    team_name: 'T-Ball',
    notes: 'First year, never played before',
    payment_status: 'paid',
    registration_status: 'confirmed',
    created_at: '2026-03-20T09:00:00',
  },
  {
    id: 'reg_004',
    organization_id: 'org_001',
    event_id: 'evt_003',
    name: 'Sandra Wiebe',
    email: 'swiebe@example.com',
    phone: '(306) 555-0534',
    team_name: '',
    notes: 'Table of 6 please',
    payment_status: 'paid',
    registration_status: 'confirmed',
    created_at: '2026-03-10T11:00:00',
  },
  {
    id: 'reg_005',
    organization_id: 'org_001',
    event_id: 'evt_004',
    name: 'Ryan Toews',
    email: 'rtoews@example.com',
    phone: '(306) 555-0645',
    team_name: 'Atom',
    notes: '',
    payment_status: 'pending',
    registration_status: 'pending',
    created_at: '2026-04-28T15:45:00',
  },
];

export const _mockSponsors: Sponsor[] = [
  {
    id: 'spo_001',
    organization_id: 'org_001',
    name: 'Westland Co-op',
    logo_url: '/assets/sponsors/coop.svg',
    website_url: 'https://westland.crs.coop',
    sponsorship_level: 'platinum',
    display_order: 1,
    active: true,
  },
  {
    id: 'spo_002',
    organization_id: 'org_001',
    name: 'Prairie Iron & Steel',
    logo_url: '/assets/sponsors/prairie-iron.svg',
    website_url: '#',
    sponsorship_level: 'gold',
    display_order: 2,
    active: true,
  },
  {
    id: 'spo_003',
    organization_id: 'org_001',
    name: 'Affinity Credit Union',
    logo_url: '/assets/sponsors/affinity.svg',
    website_url: 'https://affinitycu.ca',
    sponsorship_level: 'gold',
    display_order: 3,
    active: true,
  },
  {
    id: 'spo_004',
    organization_id: 'org_001',
    name: 'Westland Pharmasave',
    logo_url: '/assets/sponsors/pharmasave.svg',
    website_url: '#',
    sponsorship_level: 'silver',
    display_order: 4,
    active: true,
  },
  {
    id: 'spo_005',
    organization_id: 'org_001',
    name: 'Town of Westland',
    logo_url: '/assets/sponsors/town.svg',
    website_url: '#',
    sponsorship_level: 'community',
    display_order: 5,
    active: true,
  },
];

export const _mockAnnouncements: Announcement[] = [
  {
    id: 'ann_001',
    organization_id: 'org_001',
    title: 'Hockey Registration Opens September 6',
    body: 'Registration for the 2026/27 hockey season is now open. All divisions from Initiation through Midget are accepting players. Early bird pricing is available until August 20 — save $30 per player. Head to the Events page to register.',
    published: true,
    publish_date: '2026-08-01T08:00:00',
    created_at: '2026-07-30T10:00:00',
  },
  {
    id: 'ann_002',
    organization_id: 'org_001',
    title: 'Volunteer Coaches Needed for Novice Hockey',
    body: 'We are looking for volunteer coaches and assistant coaches for our Novice division this season. No previous coaching experience required — certification courses are available through Hockey Saskatchewan. Contact us to get involved.',
    published: true,
    publish_date: '2026-08-10T08:00:00',
    created_at: '2026-08-09T09:00:00',
  },
  {
    id: 'ann_003',
    organization_id: 'org_001',
    title: 'New Equipment Swap Program Launching This Fall',
    body: 'WMSA is launching a community equipment swap program this fall. Bring gently used hockey or baseball equipment and exchange it for gear in a different size. The swap table will be open during registration days and the first two weeks of the season.',
    published: true,
    publish_date: '2026-08-15T08:00:00',
    created_at: '2026-08-14T11:00:00',
  },
  {
    id: 'ann_004',
    organization_id: 'org_001',
    title: 'Fundraising Draw — Win a Yeti Cooler',
    body: 'Tickets are $5 each or 3 for $10. Draw takes place at the Season Kickoff Night on September 13. All proceeds go toward arena ice time costs. Pick up tickets from any board member or at registration.',
    published: false,
    publish_date: '2026-08-20T08:00:00',
    created_at: '2026-08-18T10:00:00',
  },
];

export const _mockAdminUsers: AdminUser[] = [
  {
    id: 'usr_001',
    organization_id: 'org_001',
    name: 'Demo Admin',
    email: 'admin@hubwire.ca',
    role: 'owner',
    active: true,
  },
  {
    id: 'usr_002',
    organization_id: 'org_001',
    name: 'Sarah Kowalski',
    email: 'sarah@westlandminorsports.ca',
    role: 'admin',
    active: true,
  },
];

export const _mockPassword = 'hubwire2026';
